"use client";

import { cn } from "@/lib/utils";
import type { ThrowballTemplateData } from "../throwball-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type ThrowballScorecardProps = {
  data: ThrowballTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function ThrowballScorecard({
  data,
  participants: _participants,
  variant = "feed",
}: ThrowballScorecardProps) {
  if (!data) return null;

  const { yourSets, opponentSets } = data.matchScore;
  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;
  const isDraw = yourSets === opponentSets;
  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const scoreTextSize = variant === "feed" ? "text-4xl" : "text-3xl";

  const content = (
    <>
      <div className="mb-3 flex items-center justify-center gap-6">
        <div className="text-center">
          <div
            className={cn(
              "font-bold",
              scoreTextSize,
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {yourSets}
          </div>
          <div className="text-xs text-text-secondary">Team 1</div>
        </div>
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <div className="text-center">
          <div
            className={cn(
              "font-bold",
              scoreTextSize,
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {opponentSets}
          </div>
          <div className="text-xs text-text-secondary">Team 2</div>
        </div>
      </div>

      {/* Result badge */}
      <div className="mb-4 flex justify-center">
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            yourWon && "bg-brand/20 text-brand",
            opponentWon && "bg-error/20 text-error",
            isDraw && "bg-text-secondary/20 text-text-secondary",
          )}
        >
          {yourWon ? "🏆 Victory!" : opponentWon ? "Defeat" : "Draw"}
        </span>
      </div>

      {/* Set details if available */}
      {data.sets && data.sets.length > 0 && (
        <div className="mb-4 flex justify-center gap-2 border-t border-border pt-2">
          {data.sets.map((set, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: set scores are positional and never reorder
              key={`set-${i}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {set.yourScore}-{set.opponentScore}
            </span>
          ))}
        </div>
      )}
    </>
  );

  if (variant === "feed") return content;
  return <ScorecardShell variant={variant}>{content}</ScorecardShell>;
}
